// Liste des créances (H4-04) : filtres (état, échéance, rubrique, nature du fait générateur),
// colonnes de l'écran de référence S11 (dû, payé avec sa réglette, reste, échéance, état), export
// CSV journalisé par le serveur, saisie d'un avis d'imposition et paiement au guichet.
import { Bandeau, Bouton, EtatVide, Filtres, Lien, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Download, FilePlus2, Receipt } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { CODES_ETAT_CREANCE, LIBELLES_ETAT_CREANCE, LIBELLES_NATURE_FAIT, francs, libelle, partPayee, tonCreance, type CreanceReponse } from './donnees';
import { FormulaireAvis } from './FormulaireAvis';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { PaiementGuichet } from './PaiementGuichet';
import { telechargerFichier, useCreances, useRubriques } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function CellulePaye({ c }: { c: CreanceReponse }) {
  const part = partPayee(c);
  return (
    <div className={styles.paye}>
      <span className={styles.mono}>{francs(c.montant_paye)}</span>
      <span className={styles.reglette} data-soldee={c.etat === 'soldee' || undefined} role="img" aria-label={`${part} % payé`}>
        <span style={{ width: `${part}%` }} />
      </span>
    </div>
  );
}

export function puceCreance(c: Pick<CreanceReponse, 'etat' | 'echue'>) {
  const t = tonCreance(c);
  return <PuceEtat ton={t.ton} libelle={t.libelle} />;
}

export function PageCreances() {
  const droits = useDroitsRecettes();
  const [params, setParams] = useSearchParams();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-creances', { etat: '', echue: '', rubrique: '', nature: '' });
  const redevable = params.get('redevable') ?? '';
  const f = { etat: filtres.etat ?? '', echue: filtres.echue ?? '', rubrique: filtres.rubrique ?? '', nature: filtres.nature ?? '', ...(redevable ? { redevable_id: redevable } : {}) };
  const liste = useCreances(f);
  const rubriques = useRubriques();
  const [avis, setAvis] = useState(params.get('nouvelle') === '1');
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const fermerAvis = () => {
    setAvis(false);
    if (params.get('nouvelle')) {
      params.delete('nouvelle');
      setParams(params, { replace: true });
    }
  };

  const colonnes: ColonneTableau<CreanceReponse>[] = [
    { id: 'numero', entete: 'Créance', enTeteLigne: true, masquable: false, valeurTri: (c) => c.numero, cellule: (c) => <Lien href={`/recettes/creances/${c.id}`} className={commun.reference}>{c.numero}</Lien> },
    { id: 'redevable', entete: 'Redevable et rubrique', valeurTri: (c) => c.redevable.nom, cellule: (c) => (
      <>
        <div>{c.redevable.nom}</div>
        <div className={styles.sous}>{c.rubrique.libelle}{c.fictif ? ' (fictif)' : ''}</div>
      </>
    ) },
    { id: 'periode', entete: 'Période', valeurTri: (c) => c.periode.debut, cellule: (c) => (c.periode.debut === c.periode.fin ? date(c.periode.debut) : `${date(c.periode.debut)} au ${date(c.periode.fin)}`) },
    { id: 'du', entete: 'Dû', numerique: true, valeurTri: (c) => c.montant_du, cellule: (c) => <span className={styles.montant}>{francs(c.montant_du)}</span> },
    { id: 'paye', entete: 'Payé', valeurTri: (c) => c.montant_paye, cellule: (c) => <CellulePaye c={c} /> },
    { id: 'reste', entete: 'Reste', numerique: true, valeurTri: (c) => c.reste, cellule: (c) => <span className={`${styles.montant} ${styles.montantFort} ${c.echue && c.reste > 0 ? styles.negatif : ''}`}>{francs(c.reste)}</span> },
    { id: 'echeance', entete: 'Échéance', valeurTri: (c) => c.echeance, cellule: (c) => date(c.echeance) },
    { id: 'etat', entete: 'État', valeurTri: (c) => c.etat, cellule: puceCreance },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Créances"
        description="Créances nées d'un fait générateur (attribution, autorisation, démarche payante) ou d'un avis d'imposition saisi. Une annulation est motivée et laisse la créance visible."
        actions={
          <>
            {droits.exporter ? (
              <Bouton
                variante="secondaire"
                icone={Download}
                onClick={() => {
                  setErreurExport(null);
                  telechargerFichier('/recettes/creances/export', 'creances.csv', f).catch((e: unknown) => setErreurExport(messageErreur(e)));
                }}
              >
                Exporter en CSV
              </Bouton>
            ) : null}
            {droits.creer ? <PaiementGuichet /> : null}
            {droits.creer ? (
              <Bouton icone={FilePlus2} onClick={() => setAvis(true)}>
                Saisir un avis d&apos;imposition
              </Bouton>
            ) : null}
          </>
        }
      />
      <NavigationFinances />
      {erreurExport ? <Bandeau type="erreur" onFermer={() => setErreurExport(null)}>Export impossible : {erreurExport}</Bandeau> : null}
      {redevable ? (
        <Bandeau type="info" onFermer={() => { params.delete('redevable'); setParams(params, { replace: true }); }}>
          Créances d&apos;un seul redevable.
        </Bandeau>
      ) : null}
      <Filtres
        libelle="Filtres des créances"
        groupes={[
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...CODES_ETAT_CREANCE.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_CREANCE[e] ?? e }))] },
          { id: 'echue', libelle: 'Échéance', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'oui', libelle: 'Échues' }, { valeur: 'non', libelle: 'Non échues' }] },
          { id: 'nature', libelle: 'Fait générateur', options: [{ valeur: '', libelle: 'Tous' }, ...Object.entries(LIBELLES_NATURE_FAIT).map(([valeur, l]) => ({ valeur, libelle: l }))] },
          { id: 'rubrique', libelle: 'Rubrique', options: [{ valeur: '', libelle: 'Toutes' }, ...(rubriques.data ?? []).filter((r) => r.categorie === 'principale').map((r) => ({ valeur: r.code, libelle: r.libelle }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des créances">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune créance" icone={Receipt}>
            <p>Aucune créance ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Créances" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(c) => c.id} cleColonnes="recettes-creances" />
        )}
      </EtatRequete>
      <p className={styles.note}>{libelle(LIBELLES_ETAT_CREANCE, 'emise')} : à payer ; échue : non soldée après l&apos;échéance (relances à J+7 et J+30) ; un paiement partiel laisse la créance partiellement payée.</p>
      {avis ? <FormulaireAvis ouvert onFermer={fermerAvis} /> : null}
    </>
  );
}
